/**
 * Text measurement utilities for calculating rendered text dimensions.
 *
 * Uses an offscreen canvas to measure text width without DOM manipulation.
 * Reads computed font from a reference element to stay in sync with CSS.
 */

let canvas: CanvasRenderingContext2D | null = null;
let cachedFont = '';

/**
 * Measures text width using canvas and computed styles from a reference element.
 *
 * @param text - The string to measure
 * @param referenceElement - DOM element whose computed font style should be used
 * @returns Width in pixels
 */
export const measureTextWidth = (text: string, referenceElement: HTMLElement): number => {
  if (!canvas) {
    const el = document.createElement('canvas');
    canvas = el.getContext('2d');
  }
  if (!canvas) return 0;

  const computed = getComputedStyle(referenceElement);
  const font = `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;

  if (font !== cachedFont) {
    cachedFont = font;
    canvas.font = font;
  }

  return canvas.measureText(text).width;
};

/**
 * Creates a hidden reference element with specific CSS classes to measure text dimensions.
 *
 * @param className - CSS classes to apply for accurate font measurement
 * @returns HTMLElement that can be used as reference for measureTextWidth
 */
export const createTextMeasurementReference = (className: string): HTMLElement => {
  const ref = document.createElement('span');
  ref.className = className;
  ref.style.position = 'absolute';
  ref.style.visibility = 'hidden';
  ref.style.pointerEvents = 'none';
  ref.setAttribute('aria-hidden', 'true');
  document.body.appendChild(ref);
  return ref;
};

/**
 * Removes a measurement reference element from the DOM.
 *
 * @param ref - The reference element to remove
 */
export const removeMeasurementReference = (ref: HTMLElement): void => {
  if (ref.parentNode) {
    ref.parentNode.removeChild(ref);
  }
};
